/**
 * Package Tracking Page - Live 3D Tracking Experience
 * Real-time driver movement and chain delivery progress visualization
 */
import { useState, useEffect, useRef, Suspense, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, RoundedBox, Environment } from '@react-three/drei';
import * as THREE from 'three';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import PackageChat from '../components/PackageChat';
import RedeliveryAttempt from '../components/RedeliveryAttempt';
import DeliveryReviewTip from '../components/DeliveryReviewTip';
import {
  Package,
  MapPin,
  Navigation,
  Clock,
  CheckCircle,
  Circle,
  Truck,
  ArrowLeft,
  RefreshCw,
  Phone,
  Star,
  ChevronRight,
  AlertCircle,
  Bike,
  MessageCircle,
  Car,
  Timer,
  Route,
  Building2,
  User,
  QrCode,
  Copy,
  Share2,
  ExternalLink
} from 'lucide-react';

// Jamaican Color Palette
const COLORS = {
  green: '#00A86B',
  yellow: '#FFD300',
  black: '#000000',
  white: '#FFFFFF',
  paleGreen: '#E8FFF4',
  darkSlate: '#0f172a',
  slate800: '#1e293b',
  slate700: '#334155',
  amber500: '#f59e0b',
};

// ============== 3D COMPONENTS ==============

// 3D Location Pin
function LocationPin3D({ position, color, label, isActive = false, scale = 1 }) {
  const meshRef = useRef();
  
  useFrame((state) => {
    if (meshRef.current && isActive) {
      meshRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 3) * 0.1;
    }
  });

  return (
    <Float speed={isActive ? 2 : 0} rotationIntensity={0} floatIntensity={isActive ? 0.3 : 0}>
      <group ref={meshRef} position={position} scale={scale}>
        {/* Pin Head */}
        <mesh position={[0, 0.5, 0]}>
          <sphereGeometry args={[0.3, 32, 32]} />
          <meshStandardMaterial 
            color={color} 
            roughness={0.3} 
            metalness={0.4}
            emissive={isActive ? color : '#000000'}
            emissiveIntensity={isActive ? 0.3 : 0}
          />
        </mesh>
        
        {/* Pin Stem */}
        <mesh position={[0, 0, 0]}>
          <coneGeometry args={[0.2, 0.5, 32]} />
          <meshStandardMaterial color={color} roughness={0.3} metalness={0.4} />
        </mesh>
        
        {/* Shadow Ring */}
        <mesh position={[0, -0.3, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.2, 0.4, 32]} />
          <meshStandardMaterial color="#000000" transparent opacity={0.2} />
        </mesh>
        
        {/* Pulse Ring (active only) */}
        {isActive && (
          <mesh position={[0, -0.3, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.4, 0.6, 32]} />
            <meshStandardMaterial color={color} transparent opacity={0.3} />
          </mesh>
        )}
      </group>
    </Float>
  );
}

// 3D Delivery Vehicle
function DeliveryVehicle3D({ position, vehicleType = 'motorcycle', heading = 0 }) {
  const groupRef = useRef();
  
  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 4) * 0.05;
    }
  });

  return (
    <group ref={groupRef} position={position} rotation={[0, heading * (Math.PI / 180), 0]}>
      {vehicleType === 'motorcycle' ? (
        <>
          {/* Motorcycle Body */}
          <mesh position={[0, 0.2, 0]}>
            <boxGeometry args={[0.3, 0.25, 0.8]} />
            <meshStandardMaterial color={COLORS.green} roughness={0.4} metalness={0.6} />
          </mesh>
          {/* Wheels */}
          <mesh position={[0, 0.05, 0.3]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.12, 0.12, 0.08, 32]} />
            <meshStandardMaterial color={COLORS.black} roughness={0.7} />
          </mesh>
          <mesh position={[0, 0.05, -0.3]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.12, 0.12, 0.08, 32]} />
            <meshStandardMaterial color={COLORS.black} roughness={0.7} />
          </mesh>
          {/* Delivery Box */}
          <RoundedBox args={[0.35, 0.3, 0.35]} radius={0.03} position={[0, 0.45, -0.15]}>
            <meshStandardMaterial color={COLORS.yellow} roughness={0.4} />
          </RoundedBox>
        </>
      ) : (
        <>
          {/* Car Body */}
          <RoundedBox args={[0.5, 0.3, 0.9]} radius={0.05} position={[0, 0.2, 0]}>
            <meshStandardMaterial color={COLORS.green} roughness={0.4} metalness={0.5} />
          </RoundedBox>
          {/* Roof */}
          <RoundedBox args={[0.45, 0.2, 0.5]} radius={0.03} position={[0, 0.4, 0.1]}>
            <meshStandardMaterial color={COLORS.green} roughness={0.4} metalness={0.5} />
          </RoundedBox>
          {/* Wheels */}
          {[[-0.25, 0.05, 0.3], [0.25, 0.05, 0.3], [-0.25, 0.05, -0.3], [0.25, 0.05, -0.3]].map((pos, i) => (
            <mesh key={i} position={pos} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.1, 0.1, 0.06, 32]} />
              <meshStandardMaterial color={COLORS.black} roughness={0.7} />
            </mesh>
          ))}
        </>
      )}
      
      {/* Direction Indicator */}
      <mesh position={[0, 0.55, 0.5]}>
        <coneGeometry args={[0.08, 0.15, 8]} rotation={[Math.PI / 2, 0, 0]} />
        <meshStandardMaterial color={COLORS.yellow} emissive={COLORS.yellow} emissiveIntensity={0.5} />
      </mesh>
    </group>
  );
}

// 3D Route Path
function RoutePath3D({ points, completed = false }) {
  const geometry = useMemo(() => {
    const curve = new THREE.CatmullRomCurve3(
      points.map(p => new THREE.Vector3(p[0], p[1], p[2]))
    );
    const tubeGeometry = new THREE.TubeGeometry(curve, 64, 0.03, 8, false);
    return tubeGeometry;
  }, [points]);

  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial 
        color={completed ? COLORS.green : COLORS.yellow} 
        roughness={0.4}
        transparent
        opacity={completed ? 1 : 0.6}
      />
    </mesh>
  );
}

// 3D Chain Link
function ChainLink3D({ position, color, rotation = [0, 0, 0], completed = false }) {
  return (
    <mesh position={position} rotation={rotation}>
      <torusGeometry args={[0.15, 0.04, 16, 32]} />
      <meshStandardMaterial 
        color={color}
        roughness={0.2}
        metalness={0.8}
        emissive={completed ? color : '#000000'}
        emissiveIntensity={completed ? 0.2 : 0}
      />
    </mesh>
  );
}

// PUDO (Pickup/Dropoff) Building
function PUDOBuilding3D({ position, name, isActive = false }) {
  return (
    <group position={position}>
      {/* Building */}
      <RoundedBox args={[0.6, 0.5, 0.6]} radius={0.05} position={[0, 0.25, 0]}>
        <meshStandardMaterial 
          color={isActive ? COLORS.green : COLORS.slate700}
          roughness={0.5}
        />
      </RoundedBox>
      
      {/* Roof */}
      <mesh position={[0, 0.55, 0]}>
        <boxGeometry args={[0.7, 0.1, 0.7]} />
        <meshStandardMaterial color={COLORS.yellow} roughness={0.4} />
      </mesh>
      
      {/* Door */}
      <mesh position={[0, 0.2, 0.31]}>
        <planeGeometry args={[0.15, 0.35]} />
        <meshStandardMaterial color={COLORS.black} />
      </mesh>
    </group>
  );
}

// Main 3D Tracking Scene
function TrackingScene3D({ trackingData, driverLocation }) {
  const completedLegs = trackingData?.tracking?.completed_legs?.length || 0;
  const totalLegs = trackingData?.tracking?.total_legs || 1;
  const hasDriver = !!driverLocation;
  
  // Generate positions for visualization
  const pickupPos = [-2, 0, 0];
  const dropoffPos = [2, 0, 0];
  const driverPos = hasDriver ? [
    (driverLocation.progress || 0.5) * 4 - 2,
    0.1,
    0
  ] : null;

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 10, 5]} intensity={1} castShadow />
      <pointLight position={[-5, 5, -5]} intensity={0.5} color={COLORS.green} />
      
      {/* Ground Plane */}
      <mesh position={[0, -0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[10, 6]} />
        <meshStandardMaterial color={COLORS.slate800} roughness={0.9} />
      </mesh>
      
      {/* Grid Lines */}
      <gridHelper args={[10, 20, COLORS.slate700, COLORS.slate700]} position={[0, 0, 0]} />
      
      {/* Route Path */}
      <RoutePath3D 
        points={[pickupPos, [0, 0.1, 0], dropoffPos]} 
        completed={completedLegs === totalLegs}
      />
      
      {/* Pickup Pin */}
      <LocationPin3D 
        position={pickupPos} 
        color={COLORS.green}
        isActive={completedLegs === 0}
        scale={0.8}
      />
      
      {/* Dropoff Pin */}
      <LocationPin3D 
        position={dropoffPos} 
        color={COLORS.yellow}
        isActive={completedLegs === totalLegs}
        scale={0.8}
      />
      
      {/* Chain Links for multi-leg delivery */}
      {totalLegs > 1 && Array.from({ length: totalLegs - 1 }).map((_, i) => {
        const progress = (i + 1) / totalLegs;
        const x = progress * 4 - 2;
        const completed = i < completedLegs;
        return (
          <group key={i}>
            <ChainLink3D 
              position={[x, 0.3, 0]}
              color={completed ? COLORS.green : COLORS.yellow}
              rotation={[Math.PI / 2, 0, 0]}
              completed={completed}
            />
            <PUDOBuilding3D 
              position={[x, 0, 0.8]}
              isActive={i === completedLegs - 1}
            />
          </group>
        );
      })}
      
      {/* Driver Vehicle */}
      {driverPos && (
        <DeliveryVehicle3D 
          position={driverPos}
          vehicleType={driverLocation?.vehicleType || 'motorcycle'}
          heading={driverLocation?.heading || 0}
        />
      )}
      
      {/* Environment */}
      <Environment preset="city" />
    </>
  );
}

// ============== 2D FALLBACK COMPONENTS ==============

// 2D Fallback Map
function FallbackMap({ trackingData, driverLocation }) {
  const completedLegs = trackingData?.tracking?.completed_legs?.length || 0;
  const totalLegs = trackingData?.tracking?.total_legs || 1;
  const progress = totalLegs > 0 ? (completedLegs / totalLegs) * 100 : 0;
  
  return (
    <div className="relative w-full h-64 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl overflow-hidden border border-slate-700">
      {/* Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `linear-gradient(${COLORS.slate700} 1px, transparent 1px), linear-gradient(90deg, ${COLORS.slate700} 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }}
      />
      
      {/* Route Line */}
      <div className="absolute top-1/2 left-[15%] right-[15%] h-2 bg-slate-700 rounded-full transform -translate-y-1/2">
        <div 
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${COLORS.green}, ${COLORS.yellow})`
          }}
        />
      </div>
      
      {/* Pickup Point */}
      <div className="absolute top-1/2 left-[15%] transform -translate-x-1/2 -translate-y-1/2">
        <div className="relative">
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/30">
            <Package className="w-5 h-5 text-white" />
          </div>
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            <span className="text-xs text-slate-400">Pickup</span>
          </div>
        </div>
      </div>
      
      {/* PUDO Stops */}
      {totalLegs > 1 && Array.from({ length: totalLegs - 1 }).map((_, i) => {
        const leftPercent = 15 + ((i + 1) / totalLegs) * 70;
        const isCompleted = i < completedLegs;
        return (
          <div 
            key={i}
            className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${leftPercent}%` }}
          >
            <div className={`w-8 h-8 rounded-lg ${isCompleted ? 'bg-green-500' : 'bg-slate-600'} flex items-center justify-center shadow-md`}>
              <Building2 className="w-4 h-4 text-white" />
            </div>
          </div>
        );
      })}
      
      {/* Dropoff Point */}
      <div className="absolute top-1/2 right-[15%] transform translate-x-1/2 -translate-y-1/2">
        <div className="relative">
          <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            <span className="text-xs text-slate-400">Dropoff</span>
          </div>
        </div>
      </div>
      
      {/* Driver Position */}
      {driverLocation && (
        <div 
          className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-1000"
          style={{ left: `${15 + (driverLocation.progress || 0.5) * 70}%` }}
        >
          <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center animate-pulse shadow-xl shadow-green-500/50 border-2 border-white">
            <Truck className="w-6 h-6 text-white" />
          </div>
        </div>
      )}
      
      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex gap-3 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-slate-400">Completed</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span className="text-slate-400">Remaining</span>
        </div>
      </div>
    </div>
  );
}

// ============== UI COMPONENTS ==============

// Timeline Step Component
function TimelineStep({ step, isLast }) {
  const getIcon = () => {
    if (step.completed) return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (step.current) return <Circle className="w-5 h-5 text-amber-500 animate-pulse" />;
    return <Circle className="w-5 h-5 text-slate-600" />;
  };

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        {getIcon()}
        {!isLast && (
          <div className={`w-0.5 h-full min-h-[40px] ${step.completed ? 'bg-green-500' : 'bg-slate-700'}`} />
        )}
      </div>
      <div className="pb-6">
        <p className={`font-medium ${step.completed || step.current ? 'text-white' : 'text-slate-500'}`}>
          {step.label}
        </p>
        {step.timestamp && (
          <p className="text-xs text-slate-400 mt-1">
            {new Date(step.timestamp).toLocaleString()}
          </p>
        )}
        {step.sub_steps && step.sub_steps.length > 0 && (
          <div className="mt-2 ml-2 space-y-1">
            {step.sub_steps.map((sub, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                {sub.completed ? (
                  <CheckCircle className="w-3 h-3 text-green-500" />
                ) : (
                  <Circle className="w-3 h-3 text-slate-600" />
                )}
                <span className={sub.completed ? 'text-slate-300' : 'text-slate-500'}>{sub.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Driver Info Card
function DriverCard({ driver }) {
  if (!driver) return null;

  const getVehicleIcon = (type) => {
    switch(type) {
      case 'motorcycle': return <Bike className="w-5 h-5" />;
      case 'car': return <Car className="w-5 h-5" />;
      default: return <Truck className="w-5 h-5" />;
    }
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700 p-4">
      <div className="flex items-center gap-4">
        {driver.photo_url || driver.profile_photo_url ? (
          <img 
            src={driver.photo_url || driver.profile_photo_url} 
            alt={driver.name}
            className="w-14 h-14 rounded-full object-cover border-2 border-green-500"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
        ) : null}
        <div className={"w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center " + (driver.photo_url || driver.profile_photo_url ? "hidden" : "")}>
          <User className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-white">{driver.name}</h4>
          <div className="flex items-center gap-3 mt-1">
            <div className="flex items-center gap-1 text-amber-400">
              <Star className="w-4 h-4 fill-current" />
              <span className="text-sm">{driver.rating?.toFixed(1) || '4.5'}</span>
            </div>
            <div className="flex items-center gap-1 text-slate-400">
              {getVehicleIcon(driver.vehicle_type)}
              <span className="text-sm capitalize">{driver.vehicle_type || 'Motorcycle'}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={() => toast.info("Contact feature coming soon!")}
          >
            <Phone className="w-4 h-4 mr-1" />
            Call
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
            onClick={() => window.dispatchEvent(new CustomEvent("openPackageChat", { detail: { driverId: driver.id } }))}
          >
            <MessageCircle className="w-4 h-4 mr-1" />
            Chat
          </Button>
        </div>
        </div>
    </Card>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const getStatusConfig = (s) => {
    const configs = {
      'created': { color: 'bg-slate-600', text: 'Order Placed' },
      'assigned': { color: 'bg-blue-600', text: 'Driver Assigned' },
      'picked_up': { color: 'bg-amber-600', text: 'Picked Up' },
      'in_chain': { color: 'bg-purple-600', text: 'In Chain Transit' },
      'in_transit': { color: 'bg-amber-600', text: 'In Transit' },
      'out_for_delivery': { color: 'bg-green-600', text: 'Out for Delivery' },
      'delivered': { color: 'bg-green-600', text: 'Delivered' },
      'cancelled': { color: 'bg-red-600', text: 'Cancelled' },
    };
    return configs[s] || { color: 'bg-slate-600', text: s };
  };

  const config = getStatusConfig(status);
  
  return (
    <Badge className={`${config.color} text-white px-3 py-1`}>
      {config.text}
    </Badge>
  );
}

// ============== MAIN COMPONENT ==============

export default function PackageTracking() {
  const { packageId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const location = window.location.pathname;
  
  const [trackingData, setTrackingData] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [use3D, setUse3D] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Determine if this is a public view based on route path or query param
  const isPublic = location.startsWith('/tracking/') || searchParams.get('public') === 'true';
  
  // Fetch tracking data
  const fetchTracking = useCallback(async () => {
    try {
      const endpoint = isPublic 
        ? `/tracking/public/${packageId}`
        : `/tracking/${packageId}`;
      
      const response = await api.get(endpoint);
      setTrackingData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching tracking:', err);
      setError(err.response?.data?.detail || 'Failed to load tracking data');
    } finally {
      setLoading(false);
    }
  }, [packageId, isPublic]);
  
  // Fetch live updates
  const fetchLiveData = useCallback(async () => {
    if (isPublic) return;
    
    try {
      const response = await api.get(`/tracking/${packageId}/live`);
      setLiveData(response.data);
      
      // Calculate progress for visualization
      if (response.data.driver_location) {
        // Simulate progress based on ETA
        const eta = response.data.eta_minutes || 30;
        const progress = Math.max(0.1, Math.min(0.9, 1 - (eta / 60)));
        setLiveData(prev => ({
          ...prev,
          driver_location: {
            ...prev?.driver_location,
            progress
          }
        }));
      }
    } catch (err) {
      console.error('Error fetching live data:', err);
    }
  }, [packageId, isPublic]);
  
  // Initial fetch
  useEffect(() => {
    if (!authLoading) {
      fetchTracking();
    }
  }, [fetchTracking, authLoading]);
  
  // Auto-refresh for live updates
  useEffect(() => {
    if (!autoRefresh || loading || isPublic) return;
    
    const interval = setInterval(() => {
      fetchLiveData();
    }, 10000); // Refresh every 10 seconds
    
    fetchLiveData(); // Initial fetch
    
    return () => clearInterval(interval);
  }, [autoRefresh, loading, isPublic, fetchLiveData]);
  
  // Copy tracking link
  const copyTrackingLink = () => {
    const url = `${window.location.origin}/track/${packageId}?public=true`;
    navigator.clipboard.writeText(url);
    toast.success('Tracking link copied!');
  };
  
  // Combine driver location data
  const driverLocation = useMemo(() => {
    if (liveData?.driver_location) {
      return {
        ...liveData.driver_location,
        vehicleType: trackingData?.tracking?.driver?.vehicle_type,
        progress: liveData.driver_location.progress || 0.5
      };
    }
    if (trackingData?.tracking?.driver?.location) {
      return {
        ...trackingData.tracking.driver.location,
        vehicleType: trackingData.tracking.driver.vehicle_type,
        progress: 0.5
      };
    }
    return null;
  }, [liveData, trackingData]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading tracking data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="bg-slate-800 border-slate-700 p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Tracking Error</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={fetchTracking}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const packageData = trackingData?.package;
  const tracking = trackingData?.tracking;
  const timeline = trackingData?.timeline || [];
  const trackingCode = packageData?.tracking_code || packageId;
  
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate(-1)}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold flex items-center gap-2">
                  <Package className="w-5 h-5 text-green-500" />
                  Package Tracking
                </h1>
                <p className="text-sm text-slate-400">
                  ID: {packageId?.substring(0, 8)}...
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={copyTrackingLink}
                className="border-slate-700 text-slate-300"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Share
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`border-slate-700 ${autoRefresh ? 'text-green-400' : 'text-slate-400'}`}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
                {autoRefresh ? 'Live' : 'Paused'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Status Banner */}
        <Card className="bg-gradient-to-r from-slate-800 to-slate-900 border-slate-700 p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <StatusBadge status={packageData?.status} />
                {tracking?.total_legs > 1 && (
                  <Badge className="bg-purple-600/20 text-purple-400 border border-purple-600/30">
                    <Route className="w-3 h-3 mr-1" />
                    Chain Delivery ({tracking.total_legs} legs)
                  </Badge>
                )}
              </div>
              <p className="text-slate-400">
                To: <span className="text-white font-medium">{packageData?.recipient_name || 'Recipient'}</span>
              </p>
            </div>
            
            <div className="text-right">
              <div className="flex items-center gap-2 text-2xl font-bold text-green-400">
                <Timer className="w-6 h-6" />
                {liveData?.eta_text || tracking?.eta_text || 'Calculating...'}
              </div>
              <p className="text-sm text-slate-400">Estimated Arrival</p>
            </div>
          </div>
        </Card>

        {/* 3D Map Visualization */}
        <Card className="bg-slate-800/50 border-slate-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Navigation className="w-5 h-5 text-green-500" />
              Live Tracking Map
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setUse3D(!use3D)}
              className="text-slate-400 hover:text-white"
            >
              {use3D ? '2D View' : '3D View'}
            </Button>
          </div>
          
          {use3D ? (
            <div className="h-72 rounded-xl overflow-hidden bg-slate-900">
              <Suspense fallback={
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full" />
                </div>
              }>
                <Canvas
                  camera={{ position: [0, 4, 6], fov: 50 }}
                  gl={{ antialias: true, alpha: true }}
                  onCreated={({ gl }) => {
                    gl.setClearColor(COLORS.darkSlate);
                  }}
                >
                  <TrackingScene3D 
                    trackingData={trackingData} 
                    driverLocation={driverLocation}
                  />
                </Canvas>
              </Suspense>
            </div>
          ) : (
            <FallbackMap 
              trackingData={trackingData}
              driverLocation={driverLocation}
            />
          )}
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Timeline */}
          <div className="md:col-span-2">
            <Card className="bg-slate-800/50 border-slate-700 p-6">
              <h3 className="font-semibold mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Delivery Timeline
              </h3>
              
              <div className="space-y-0">
                {timeline.map((step, index) => (
                  <TimelineStep 
                    key={index}
                    step={step}
                    isLast={index === timeline.length - 1}
                  />
                ))}
              </div>
            </Card>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* Driver Info */}
            {tracking?.driver && (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-green-500" />
                  Your Driver
                </h3>
                <DriverCard driver={tracking.driver} />
              </div>
            )}

            {/* Package Details */}
            <Card className="bg-slate-800/50 border-slate-700 p-4">
              <h4 className="font-semibold mb-4 text-sm text-slate-400 uppercase tracking-wide">
                Package Details
              </h4>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Pickup</p>
                    <p className="text-sm text-white">
                      {packageData?.pickup_location?.address || 'Pickup Location'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Dropoff</p>
                    <p className="text-sm text-white">
                      {packageData?.dropoff_location?.address || 'Delivery Location'}
                    </p>
                  </div>
                </div>

                {packageData?.qr_code_data && (
                  <div className="pt-3 border-t border-slate-700">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-slate-600 text-slate-300"
                      onClick={() => {
                        navigator.clipboard.writeText(packageData.qr_code_data);
                        toast.success('QR code data copied!');
                      }}
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      Copy QR Code
                    </Button>
                  </div>
                )}

                {/* Redelivery Option */}
                <RedeliveryAttempt 
                  packageData={packageData} 
                  onSuccess={() => fetchTracking()} 
                />
              </div>
            </Card>

            {/* Chain Delivery Info */}
            {tracking?.total_legs > 1 && (
              <Card className="bg-slate-800/50 border-slate-700 p-4">
                <h4 className="font-semibold mb-4 text-sm text-slate-400 uppercase tracking-wide">
                  Chain Delivery Progress
                </h4>
                
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
                      style={{ 
                        width: `${(tracking.completed_legs?.length / tracking.total_legs) * 100}%` 
                      }}
                    />
                  </div>
                  <span className="text-sm text-slate-400">
                    {tracking.completed_legs?.length || 0}/{tracking.total_legs}
                  </span>
                </div>
                
                <div className="space-y-2">
                  {tracking.completed_legs?.map((leg, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-slate-300">Leg {leg.sequence_number} completed</span>
                    </div>
                  ))}
                  {tracking.current_leg && (
                    <div className="flex items-center gap-2 text-sm">
                      <Circle className="w-4 h-4 text-amber-500 animate-pulse" />
                      <span className="text-amber-400">Leg {tracking.current_leg.sequence_number} in progress</span>
                    </div>
                  )}
                  {tracking.future_legs?.map((leg, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Circle className="w-4 h-4 text-slate-600" />
                      <span className="text-slate-500">Leg {leg.sequence_number} pending</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Delivered: Review & Tip */}
        {packageData?.status === 'delivered' && !isPublic && (
          <DeliveryReviewTip
            packageId={packageId}
            driverName={tracking?.driver?.name}
            trackingCode={packageData?.tracking_code || packageId}
          />
        )}

      {/* Package Chat */}
      {showChat && trackingCode && (
        <PackageChat
          trackingCode={trackingCode}
          chatType={user ? (trackingData?.sender_id === user.id ? 'sender_recipient' : 'recipient_driver') : 'recipient_driver'}
          onClose={() => setShowChat(false)}
          floating={true}
        />
      )}

      {/* Floating Chat Button */}
      {!showChat && trackingCode && (
        <button
          onClick={() => setShowChat(true)}
          className="fixed bottom-6 right-6 bg-emerald-500 text-white p-4 rounded-full shadow-lg hover:bg-emerald-600 transition-all z-40"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}
      </main>
    </div>
  );
}
